import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tab, Tabs, TextField, Typography, Zoom, styled } from '@mui/material';
import Tooltip from '../../../Shared/CopyableTooltip';
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import WidgetsIcon from "@mui/icons-material/Widgets";
import ImageIcon from "@mui/icons-material/Image";
import { useNewsletterEditor } from "../context/NewsletterEditorContext";
import { formatStorageDisplayName } from "../utils/storageDisplayUtils";
import { findNodeById } from "../model/documentModel";
import { getColumnSlotState } from "../model/columnContentUtils";
import { listComponentPresets } from "../model/defaultComponents";
import { extractLayoutBlockForSave } from "../model/documentMutations";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import AddGridDialog from "../dialogs/AddGridDialog";
import {
  deleteNewsletterImage,
  deleteNewsletterLayout,
  deleteNewsletterComponent,
  listNewsletterComponents,
  listNewsletterImages,
  listNewsletterLayouts,
  loadNewsletterComponent,
  loadNewsletterLayout,
  saveNewsletterLayout,
  uploadNewsletterImage,
} from "../../../../services/newsletterStorageService";
import { ALLOWED_IMAGE_TYPES, IMAGE_MAX_SIZE_BYTES } from "../../../../config/newsletterEditorConfig";

export const DRAG_TYPE = "newsletter/component";
export const DRAG_TYPE_LAYOUT = "newsletter/layout";
export const DRAG_TYPE_IMAGE = "newsletter/image";

const UnifiedDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: theme.spacing(2),
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  },
}));

const LeftPanel = () => {
  const {
    selection,
    insertComponent,
    appendLayout,
    document,
    showEditorMessage,
    refreshImageLibrary,
    imagesRevision,
    savedComponentSignal,
    clearSavedComponentSignal,
    addGridToLayout,
  } = useNewsletterEditor();
  const [tab, setTab] = useState(0);
  const [images, setImages] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [savedComponents, setSavedComponents] = useState([]);
  const [savedComponentsData, setSavedComponentsData] = useState({});
  const [layoutsData, setLayoutsData] = useState({});
  const [confirmState, setConfirmState] = useState(null);
  const [saveLayoutOpen, setSaveLayoutOpen] = useState(false);
  const [addLayoutOpen, setAddLayoutOpen] = useState(false);
  const [layoutName, setLayoutName] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");
  const [replaceConfirm, setReplaceConfirm] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const loadImages = async () => {
    setImages(await listNewsletterImages());
  };

  const loadLayouts = async () => {
    const items = await listNewsletterLayouts();
    setLayouts(items);
    const entries = await Promise.all(
      items.map(async (item) => {
        try {
          return [item.objectKey, await loadNewsletterLayout(item.objectKey)];
        } catch {
          return [item.objectKey, null];
        }
      })
    );
    setLayoutsData(Object.fromEntries(entries.filter(([, grid]) => grid != null)));
  };

  const loadComponents = async () => {
    const items = await listNewsletterComponents();
    setSavedComponents(items);
    const entries = await Promise.all(
      items.map(async (item) => {
        try {
          return [item.objectKey, await loadNewsletterComponent(item.objectKey)];
        } catch {
          return [item.objectKey, null];
        }
      })
    );
    setSavedComponentsData(Object.fromEntries(entries.filter(([, data]) => data != null)));
  };

  const resolveSavedComponent = async (objectKey) => {
    if (savedComponentsData[objectKey]) {
      return savedComponentsData[objectKey];
    }
    const loaded = await loadNewsletterComponent(objectKey);
    setSavedComponentsData((current) => ({ ...current, [objectKey]: loaded }));
    return loaded;
  };

  useEffect(() => {
    if (!savedComponentSignal) {
      return;
    }
    const { summary, payload } = savedComponentSignal;
    setSavedComponents((current) => {
      if (current.some((item) => item.objectKey === summary.objectKey)) {
        return current;
      }
      return [summary, ...current];
    });
    setSavedComponentsData((current) => ({
      ...current,
      [summary.objectKey]: payload,
    }));
    clearSavedComponentSignal();
  }, [savedComponentSignal, clearSavedComponentSignal]);

  useEffect(() => {
    const loaders = [loadLayouts, loadComponents, loadImages];
    loaders[tab]?.().catch((err) => {
      const message = err?.message || "Impossibile caricare la libreria.";
      const isStorageUnavailable = /storage.*non disponibile/i.test(message);
      showEditorMessage({
        title: "Errore caricamento libreria",
        message,
        ...(isStorageUnavailable
          ? { detail: "Verifica che MinIO/S3 sia raggiungibile e riprova." }
          : {}),
      });
    });
  }, [tab, imagesRevision, showEditorMessage]);

  const requireCanvasHasLayout = () => {
    if (!(document.layout?.grids?.length > 0)) {
      showEditorMessage({
        title: "Nessun layout nel canvas",
        message: "Aggiungi almeno un layout alla email prima di inserire componenti.",
        detail: "Usa «Aggiungi nuovo layout» nel pannello Layout a sinistra o inserisci un layout dalla libreria.",
      });
      return false;
    }
    return true;
  };

  const requireColumn = () => {
    if (!requireCanvasHasLayout()) {
      return false;
    }
    if (!selection?.id || selection.type !== "column") {
      showEditorMessage({
        title: "Cella non selezionata",
        message: "Seleziona una cella nel canvas prima di inserire un componente.",
      });
      return false;
    }
    return true;
  };

  const requireLayoutSelection = () => {
    if (!selection?.id || selection.type !== "grid") {
      showEditorMessage({
        title: "Layout non selezionato",
        message: "Seleziona un layout nel canvas per salvarlo nella libreria.",
      });
      return null;
    }
    const found = findNodeById(document.layout, selection.id);
    if (!found || found.nodeType !== "grid") {
      showEditorMessage({
        title: "Layout non trovato",
        message: "Il layout selezionato non è più presente nel documento.",
        detail: "Seleziona di nuovo un layout valido nel canvas.",
      });
      return null;
    }
    return found.node;
  };

  const runInsertComponent = (columnId, componentKey, propsOverride, replace = false) => {
    const result = insertComponent(columnId, componentKey, propsOverride, { replace });
    if (result === "blocked_nested_grid") {
      showEditorMessage({
        title: "Cella non disponibile",
        message: "Questa cella contiene un layout interno.",
        detail: "Rimuovi il layout interno prima di inserire un componente.",
      });
      return;
    }
    if (result === "blocked") {
      setReplaceConfirm({ columnId, componentKey, propsOverride });
    }
  };

  const handleComponentClick = (componentKey, propsOverride = null) => {
    if (!requireColumn()) {
      return;
    }
    const columnNode = findNodeById(document.layout, selection.id)?.node;
    const slot = columnNode ? getColumnSlotState(columnNode) : null;
    if (slot?.hasNestedGrid) {
      showEditorMessage({
        title: "Cella non disponibile",
        message: "Questa cella contiene un layout interno.",
        detail: "Rimuovi il layout interno prima di inserire un componente.",
      });
      return;
    }
    runInsertComponent(selection.id, componentKey, propsOverride, false);
  };

  const handleSaveLayout = async () => {
    const grid = requireLayoutSelection();
    if (!grid) {
      return;
    }
    if (!layoutName.trim()) {
      showEditorMessage({
        title: "Nome obbligatorio",
        message: "Inserisci un nome per il layout da salvare.",
      });
      return;
    }
    try {
      await saveNewsletterLayout(extractLayoutBlockForSave(grid), layoutName.trim());
      setSaveLayoutOpen(false);
      setLayoutName("");
      await loadLayouts();
    } catch (err) {
      showEditorMessage({
        title: "Salvataggio layout non riuscito",
        message: err.message,
      });
    }
  };

  const handleAppendLayout = async (objectKey) => {
    try {
      const grid = await loadNewsletterLayout(objectKey);
      appendLayout(grid);
    } catch (err) {
      showEditorMessage({
        title: "Inserimento layout non riuscito",
        message: err.message,
      });
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > IMAGE_MAX_SIZE_BYTES) {
      showEditorMessage({
        title: "File non valido",
        message: "L'immagine non rispetta i requisiti di caricamento.",
        detail: "Formati ammessi: JPEG, PNG, GIF, WebP. Dimensione massima: 2 MB.",
      });
      return;
    }
    try {
      setUploadingImage(true);
      await uploadNewsletterImage(file);
      await loadImages();
      refreshImageLibrary();
    } catch (err) {
      showEditorMessage({
        title: "Caricamento immagine non riuscito",
        message: err.message,
      });
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const presetCards = useMemo(() => listComponentPresets(), []);

  const handleCopyImageUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback(url);
      setTimeout(() => setCopyFeedback(""), 2000);
    } catch {
      showEditorMessage({
        title: "Copia URL non riuscita",
        message: "Impossibile copiare l'URL negli appunti.",
        detail: "Verifica i permessi del browser per la clipboard.",
      });
    }
  };

  const openSaveLayoutDialog = () => {
    const grid = requireLayoutSelection();
    if (!grid) {
      return;
    }
    setLayoutName(grid.name || "");
    setSaveLayoutOpen(true);
  };

  const resolveSavedLayout = async (objectKey) => {
    if (layoutsData[objectKey]) {
      return layoutsData[objectKey];
    }
    const loaded = await loadNewsletterLayout(objectKey);
    setLayoutsData((current) => ({ ...current, [objectKey]: loaded }));
    return loaded;
  };

  const renderStorageList = (items, { kind, applyLabel, onApply, onDelete, deleteTitle, deleteMessage, draggable = false }) => {
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Nessun elemento salvato.
        </Typography>
      );
    }
    return items.map((item) => (
      <Card
        key={item.objectKey}
        variant="outlined"
        draggable={draggable}
        onMouseDown={
          draggable
            ? () => {
                resolveSavedLayout(item.objectKey).catch((err) =>
                  showEditorMessage({
                    title: "Caricamento layout non riuscito",
                    message: err.message,
                  })
                );
              }
            : undefined
        }
        onDragStart={
          draggable
            ? (e) => {
                const saved = layoutsData[item.objectKey];
                if (saved) {
                  e.dataTransfer.setData(`${DRAG_TYPE_LAYOUT}/saved`, JSON.stringify(saved));
                }
              }
            : undefined
        }
        sx={{ cursor: draggable ? "grab" : "default" }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="subtitle2">{formatStorageDisplayName(item, kind)}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={() => onApply(item.objectKey)}>
              {applyLabel}
            </Button>
            <IconButton
              size="small"
              aria-label={deleteTitle}
              onClick={() =>
                setConfirmState({
                  title: deleteTitle,
                  message: deleteMessage,
                  confirmColor: "#b30047",
                  onConfirm: async () => {
                    try {
                      await onDelete(item.objectKey);
                      setConfirmState(null);
                    } catch (err) {
                      setConfirmState(null);
                      showEditorMessage({
                        title: "Eliminazione non riuscita",
                        message: err.message,
                      });
                    }
                  },
                })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    ));
  };

  return (
    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, backgroundColor: "#fff", p: 1.5, height: "100%", overflow: "auto" }}>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.5, minHeight: 40 }}
      >
        {[
          { icon: <DashboardCustomizeIcon fontSize="small" />, label: "Layout" },
          { icon: <WidgetsIcon fontSize="small" />, label: "Componenti" },
          { icon: <ImageIcon fontSize="small" />, label: "Immagini" },
        ].map((item) => (
          <Tooltip key={item.label} title={item.label} arrow>
            <Tab icon={item.icon} aria-label={item.label} sx={{ minWidth: 40, minHeight: 40, p: 0.75 }} />
          </Tooltip>
        ))}
      </Tabs>

      {copyFeedback ? (
        <Typography variant="caption" color="success.main" sx={{ display: "block", mb: 1 }}>
          URL copiato negli appunti
        </Typography>
      ) : null}

      {tab === 0 ? (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: '#1976d2', fontSize: '1.15em', fontWeight: 500 }}>Layouts</span> - blocchi strutturali definiti da righe e colonne.
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            sx={{ borderRadius: 0, bgcolor: "#0b3d87" }}
            onClick={() => setAddLayoutOpen(true)}
          >
            Aggiungi nuovo layout
          </Button>
          <Button startIcon={<SaveIcon />} variant="outlined" size="small" onClick={openSaveLayoutDialog}>
            Salva layout selezionato
          </Button>
          {renderStorageList(layouts, {
            kind: "layout",
            applyLabel: "Inserisci",
            draggable: true,
            onApply: (objectKey) => handleAppendLayout(objectKey),
            onDelete: async (objectKey) => {
              await deleteNewsletterLayout(objectKey);
              await loadLayouts();
            },
            deleteTitle: "Elimina layout salvato",
            deleteMessage: "Eliminare questo layout dalla libreria?",
          })}
        </Stack>
      ) : null}

      {tab === 1 ? (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: "#1976d2", fontSize: "1.15em", fontWeight: 500 }}>Componenti</span> - blocchi
            contenuto da inserire nei layouts.
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
            Componenti base
          </Typography>
          {presetCards.map((preset) => (
            <Card
              key={preset.key}
              variant="outlined"
              draggable
              onDragStart={(e) => e.dataTransfer.setData(DRAG_TYPE, preset.key)}
              sx={{ cursor: "grab" }}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="subtitle2">{preset.label}</Typography>
                <Button size="small" sx={{ mt: 1 }} onClick={() => handleComponentClick(preset.key)}>
                  Inserisci
                </Button>
              </CardContent>
            </Card>
          ))}
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", mt: 1 }}>
            Componenti salvati
          </Typography>
          {savedComponents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Modifica un componente e salvalo dal pannello proprietà.
            </Typography>
          ) : (
            savedComponents.map((item) => (
              <Card
                key={item.objectKey}
                variant="outlined"
                draggable
                onMouseDown={() => {
                  resolveSavedComponent(item.objectKey).catch((err) =>
                    showEditorMessage({
                      title: "Caricamento componente non riuscito",
                      message: err.message,
                    })
                  );
                }}
                onDragStart={(e) => {
                  const saved = savedComponentsData[item.objectKey];
                  if (saved) {
                    e.dataTransfer.setData(`${DRAG_TYPE}/saved`, JSON.stringify(saved));
                  }
                }}
                sx={{ cursor: "grab" }}
              >
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="subtitle2">{formatStorageDisplayName(item, "component")}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          const saved = await resolveSavedComponent(item.objectKey);
                          handleComponentClick(saved.componentKey, saved.props);
                        } catch (err) {
                          showEditorMessage({
                            title: "Inserimento componente non riuscito",
                            message: err.message,
                          });
                        }
                      }}
                    >
                      Inserisci
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Elimina componente"
                      onClick={() =>
                        setConfirmState({
                          title: "Elimina componente",
                          message: "Eliminare questo componente salvato?",
                          confirmColor: "#b30047",
                          onConfirm: async () => {
                            try {
                              await deleteNewsletterComponent(item.objectKey);
                              await loadComponents();
                              setConfirmState(null);
                            } catch (err) {
                              setConfirmState(null);
                              showEditorMessage({
                                title: "Eliminazione non riuscita",
                                message: err.message,
                              });
                            }
                          },
                        })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      ) : null}

      {tab === 2 ? (
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: "#1976d2", fontSize: "1.15em", fontWeight: 500 }}>Immagini</span> — trascina
            un&apos;immagine in una cella del canvas per creare un componente immagine con URL già impostato.
          </Typography>
          <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} disabled={uploadingImage}>
            {uploadingImage ? "Caricamento..." : "Carica immagine"}
            <input type="file" hidden accept={ALLOWED_IMAGE_TYPES.join(",")} onChange={handleUpload} />
          </Button>
          <Stack spacing={1}>
            {images.map((image) => (
              <Card
                key={image.objectKey}
                variant="outlined"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    DRAG_TYPE_IMAGE,
                    JSON.stringify({
                      publicUrl: image.publicUrl,
                      displayName: formatStorageDisplayName(image, "image"),
                    })
                  );
                  e.dataTransfer.effectAllowed = "copy";
                }}
                sx={{ cursor: "grab" }}
              >
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, "&:last-child": { pb: 1 } }}>
                  <Box
                    component="img"
                    src={image.publicUrl}
                    alt={image.fileName}
                    draggable={false}
                    sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1, pointerEvents: "none" }}
                  />
                  <Typography variant="caption" sx={{ flex: 1, wordBreak: "break-all" }}>
                    {formatStorageDisplayName(image, "image")}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Copia URL immagine"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => handleCopyImageUrl(image.publicUrl)}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Elimina immagine"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() =>
                      setConfirmState({
                        title: "Elimina immagine",
                        message: "Eliminare l'immagine dalla libreria?",
                        confirmColor: "#b30047",
                        onConfirm: async () => {
                          try {
                            await deleteNewsletterImage(image.objectKey);
                            await loadImages();
                            setConfirmState(null);
                          } catch (err) {
                            setConfirmState(null);
                            showEditorMessage({
                              title: "Eliminazione non riuscita",
                              message: err.message,
                            });
                          }
                        },
                      })
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Stack>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
        onConfirm={() => confirmState?.onConfirm?.()}
        title={confirmState?.title || ""}
        message={confirmState?.message || ""}
        confirmColor={confirmState?.confirmColor}
      />

      <ConfirmDialog
        open={Boolean(replaceConfirm)}
        onClose={() => setReplaceConfirm(null)}
        onConfirm={() => {
          const pending = replaceConfirm;
          setReplaceConfirm(null);
          if (pending) {
            runInsertComponent(pending.columnId, pending.componentKey, pending.propsOverride, true);
          }
        }}
        title="Sostituire il componente?"
        message="La cella contiene già un componente. Sostituirlo con quello nuovo?"
        confirmColor="#0b3d87"
      />

      <AddGridDialog
        open={addLayoutOpen}
        onClose={() => setAddLayoutOpen(false)}
        title="Aggiungi nuovo layout"
        onConfirm={(rows, columns) => {
          addGridToLayout(rows, columns);
          setAddLayoutOpen(false);
        }}
      />

      <UnifiedDialog open={saveLayoutOpen} onClose={() => setSaveLayoutOpen(false)} maxWidth="xs" fullWidth slots={{ transition: Zoom }}>
        <DialogTitle sx={{ textAlign: "center", color: "#0b3d87", fontWeight: 700 }}>Salva layout</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome layout"
            value={layoutName}
            onChange={(e) => setLayoutName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={() => setSaveLayoutOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveLayout} disabled={!layoutName.trim()}>
            Salva
          </Button>
        </DialogActions>
      </UnifiedDialog>
    </Box>
  );
};

export default LeftPanel;
 